import { client } from "./client";

// Map a file's functions, following its in-repo includes/imports.
export function apiMapFile(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  filePath: string,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/map_file`,
    { file_path: filePath }
  );
}

// Reload the function list for an already-mapped file (no re-map).
export function apiFileFunctions(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  filePath: string,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/file_functions`,
    { file_path: filePath }
  );
}

// Full transitive program slice (code blocks + depth) rooted at a function.
export function apiFeatureSlice(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
  maxDepth?: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/feature_slice`,
    { method_id: methodId, max_depth: maxDepth ?? null }
  );
}

// Full transitive reverse slice (callers of callers) rooted at a function — the
// set of functions that use it, with code blocks + depth.
export function apiReverseSlice(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
  maxDepth?: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/reverse_slice`,
    { method_id: methodId, max_depth: maxDepth ?? null }
  );
}

// Direct dependencies (callees) of a function, enriched with class/file/line.
export function apiFunctionCallees(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/function_callees`,
    { method_id: methodId }
  );
}

// Program slice as a subgraph: all functions transitively called by this one
// (across files) + the dependency edges among them.
export function apiFunctionSlice(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
  maxDepth?: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/function_slice`,
    { method_id: methodId, max_depth: maxDepth ?? null }
  );
}

// Source code of one function (its extracted block) — for the "Show code" modal.
export function apiMethodCode(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/method_code`,
    { method_id: methodId }
  );
}

// Full source of a file — for the "Show code" modal on a file node.
export function apiFileCode(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  filePath: string,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/file_code`,
    { file_path: filePath }
  );
}

// Direct callers (used-by) of a function, enriched with class/file/line — the
// upstream mirror of apiFunctionCallees.
export function apiFunctionCallers(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/function_callers`,
    { method_id: methodId }
  );
}

// File-level dependency graph for the navigable network chart: file nodes +
// aggregated cross-file edges. Served from whatever the background worker has
// indexed so far.
export function apiFileGraph(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/file_graph`
  );
}

// FEATURE mode: the launch points (methods flagged is_feature = 1). Each is the
// on-ramp of a feature; the overlay renders these as distinct nodes.
export function apiFeatureFunctions(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/feature_functions`
  );
}

// FEATURE mode: the dependency tree (flow spine) rooted at every launch point —
// one call returns all feature trees, which we flatten into nodes + edges.
export function apiFeatureTrees(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/feature_trees`
  );
}

// FEATURE mode: the semantic-overlay anchors (curated launch points etc.) with
// their drift state, plus drift counts. Optionally filter to one drift_state.
export function apiListAnchors(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  drift?: string,
) {
  const q = drift ? `?drift=${encodeURIComponent(drift)}` : "";
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/anchors${q}`
  );
}

// FEATURE mode: re-run the re-anchor pass on demand; returns the drift summary.
export function apiReanchor(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/reanchor`,
    {}
  );
}

// FEATURE mode: anchor an existing method as a launch point by its id. The
// server resolves the coordinate and flips is_feature; it then shows in FEATURE
// mode as an anchored (gold) launch point.
export function apiAnchorMethod(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  methodId: number,
  ownerKind: string = "launch",
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/anchor_method`,
    { method_id: methodId, owner_kind: ownerKind }
  );
}

// Read-only list of candidate entry points (IBAction methods not yet features).
// Suggestions to confirm via "Mark as feature"; does not mutate anything.
export function apiLaunchCandidates(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/launch_candidates`
  );
}

// FEATURE mode: guard conditions on a call edge (caller→callee) — one entry per
// call site with its branch-guard chain, plus whether any site is unconditional.
export function apiEdgeGuards(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  callerMethodId: number,
  calleeMethodId: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/edge_guards`,
    { caller_method_id: callerMethodId, callee_method_id: calleeMethodId }
  );
}

// FEATURE mode: clear all designated features (feature/launch anchors +
// is_feature flags). Destructive — confirm in the UI first. Bundles are kept.
export function apiClearFeatures(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/clear_features`,
    {}
  );
}

// FEATURE mode: bundles (districts) with their rules and resolved member file
// paths. Membership is recomputed server-side from the live trawl tables.
export function apiListBundles(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/bundles`
  );
}

// Enqueue a background (re)index job for the repo. jobType: "FULL" | "INCREMENTAL".
// changedFiles is an optional list of repo-relative paths (e.g. from a git pull).
export function apiIndexBackground(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  jobType?: "FULL" | "INCREMENTAL" | "PURGE",
  changedFiles?: string[],
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/index_background`,
    { job_type: jobType ?? "INCREMENTAL", changed_files: changedFiles ?? null }
  );
}

// Latest background-index job status for the repo (for progress + "graph ready").
export function apiIndexStatus(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/index_status`
  );
}

// Persisted dependency-network settings (sizing POV + hidden directories).
export function apiGetGraphSettings(
  organizationId: number,
  workspaceId: number,
  repoId: number,
) {
  return client.get(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/graph_settings`
  );
}

export function apiSaveGraphSettings(
  organizationId: number,
  workspaceId: number,
  repoId: number,
  pov: string,
  hiddenDirs: string[],
  nodeColors: Record<string, string>,
) {
  return client.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/repos/${repoId}/structures/graph_settings`,
    { pov, hidden_dirs: hiddenDirs, node_colors: nodeColors }
  );
}
